import React, { useCallback, useEffect, useMemo, useState } from 'react';
import WeekTimecardGrid, { TIMES_FOLD_FIELDS } from './WeekTimecardGrid';
import PhoneActionBar from '../ui/PhoneActionBar';
import useCalcPreview from './useCalcPreview';
import ChecklistDropdown from '../ui/ChecklistDropdown';
import { BTN, BTN_XS } from '../../constants/buttons';
import { FORM_INPUT_FIELD } from '../../constants/inputs';
import { saveManagerPersonDay, saveUnitDays } from '../../utils/timesheetsApi';
import ConfirmDialog from '../ConfirmDialog';
import { formatDateDayMonth } from '../../utils/dateFormat';
import useEditReason from './useEditReason';
import TimesheetHistoryPanel from './TimesheetHistoryPanel';
import {
  BLANK_FLAT_TITLE, WORKSHOP_ON_SET_WARNING, workshopOnShootingUnit, DAY_TYPE_OPTIONS, WORKING_DAY_OPTIONS, GRID_BOLD_FIELDS, GRID_ROW_GROUPS, UNIT_FIELD, controlValue, dayLabel, fieldKind, fieldLabel, gridCellText,
  FLAT_GREYED_FIELDS, REST_CLEARS_FIELDS, fieldEditorProps, payloadValue, saveErrorText, unitNameIn, markRequiredRows, PICKED_WORKING_DAYS,
} from '../../utils/timesheetDisplay';

/**
 * THE GENERIC UNIT TIMECARD on the week grid (spec §5; owner-approved mock,
 * 2026-10-25). "Apply to" picks the units (one dropdown checklist, each with
 * its head-count — owner: shooting units "are kind of like sub departments",
 * so ONE list); each day COLUMN is that day's unit value. It applies to
 * everyone in those units; anyone with their own value keeps it, and the save's
 * response conflicts appear on the people's cells (no banner).
 *
 * Only TOUCHED cells are sent — a key omitted keeps its stored value, a cell
 * cleared sends `null` — so saving Monday's call never wipes Tuesday's wrap.
 * The request is the one it always was (`PUT /api/timesheets/unit-days`,
 * `{ sub_department_ids, days: [{ work_date, values }] }`); a day's `values`
 * now carry that COLUMN's edits, so two days can differ in one save.
 * A cell shows the selected units' stored value when they agree, "Mixed"
 * when they do not, and a blank when nothing is stored.
 */

const UNIT_ROW_GROUPS = GRID_ROW_GROUPS
  .map((g) => g.filter((f) => f !== UNIT_FIELD));
/** The unit's own CALCULATED rows (the server's `units[].days[d].calc`, 2026-10-26). */
const UNIT_CALC_FIELDS = Object.freeze(['camera_ot_hours', 'non_camera_ot_hours', 'meal_penalty']);
/**
 * The times the calculation reads: a draft of any re-asks the server as you type (2026-10-26).
 * 2026-10-28: + the unit's Pre-call / Final out (its own O/T), and Day type (a Rest day has no auto wrap).
 */
const UNIT_CALC_INPUTS = Object.freeze(['day_type', 'working_day', 'unit_call', 'unit_wrap', 'meal_start', 'meal_end', 'in_time', 'out_time']);
/** The calculated rows: a greyed Flat day shows their zero as "–". */
const OVERRIDE_LIKE = Object.freeze(['camera_ot_hours', 'non_camera_ot_hours', 'meal_penalty']);
/** Sat / Sun (owner 2026-10-27: Rest by default). */
const isWeekend = (date) => { const d = new Date(`${date}T00:00:00Z`).getUTCDay(); return d === 0 || d === 6; };
/** The times the Prep auto-fill fills (the server's `units[].prep_usual`). */
const PREP_FILL_FIELDS = Object.freeze(['unit_call', 'meal_start', 'meal_end', 'unit_wrap']);

/** How many people are on any of unit `ids` this week (a presentation count). */
export const unitHeadCount = (people, ids) => (people || [])
  .filter((p) => (p.days || []).some((d) => ids.includes(Number(d.unit_id)))).length;

/**
 * THE Apply to list (owner, 2026-10-25: "arange the list of sub dept by crew size, then pick
 * the top one as default"): head-count DESC, ties A–Z. Shared with the phone sheet.
 */
export const orderUnitsByCrew = (units, people) => (units || [])
  .map((u) => ({ id: Number(u.id), label: u.name, count: unitHeadCount(people, [Number(u.id)]) }))
  .sort((a, b) => b.count - a.count || String(a.label).localeCompare(String(b.label)));

/** The ids Apply to sends: the pick, else the biggest unit. */
export const appliedUnitIds = (picked, ordered) => (picked != null ? picked : (ordered.length ? [ordered[0].id] : []));

export default function UnitWeekTimecard({
  units, dates, today, people, onSaved, onDirtyChange, saveRef, discardRef, onSubmitWeek, submitBusy = false, dayNumbers = {},
  // 📱 Controlled Apply to (2026-10-27): the manager screen owns it so the phone sheet can set it;
  // the card then hides its own Apply to row below `md`.
  picked: pickedProp, onPickedChange,
  onStepWeek = null, // 📱 the phone strip's end cards (WeekTimecardGrid)
  requestedWeek = null,
}) {
  const controlled = typeof onPickedChange === 'function';
  // `null` = the manager has not chosen yet → the DEFAULT (the biggest unit) applies.
  const [pickedState, setPickedState] = useState(null);
  const picked = controlled ? (pickedProp ?? null) : pickedState;
  const setPicked = controlled ? onPickedChange : setPickedState;
  const [drafts, setDrafts] = useState({}); // date → { field: raw }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [savedNote, setSavedNote] = useState(null);
  const [dayAsk, setDayAsk] = useState(null); // { date, raw, from }
  // EDIT AFTER SUBMIT (2026-10-29): a unit save reaching SUBMITTED weeks asks ONE reason and lists
  // them (`useEditReason`); "History" shows each applied unit's mass edits for the week.
  const { run: runEditGate, dialog: editReasonDialog } = useEditReason({ unitName: (id) => unitNameIn(units, id) });
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyTick, setHistoryTick] = useState(0);

  const touched = Object.keys(drafts).length > 0;
  useEffect(() => { onDirtyChange?.(touched); }, [touched, onDirtyChange]);

  // A new week drops the edits: they were typed against that week's days.
  const datesKey = dates.join('|');
  useEffect(() => { setDrafts({}); setSavedNote(null); }, [datesKey]);

  const headCount = useCallback((ids) => unitHeadCount(people, ids), [people]);
  const ordered = useMemo(() => orderUnitsByCrew(units, people), [units, people]);
  const unitIds = appliedUnitIds(picked, ordered);
  const selected = useMemo(
    () => units.filter((u) => unitIds.includes(Number(u.id))),
    [units, unitIds]
  );

  const dayTypeOptions = [{ value: '', label: 'Not set' }, ...DAY_TYPE_OPTIONS];
  const workingDayOptions = [{ value: '', label: 'Flat (default)' }, ...WORKING_DAY_OPTIONS];
  const groups = [
    // Calculated overtime (2026-10-26, the owner-approved mock): who is on this unit each day.
    [{ key: 'crew', label: 'Crew', kind: 'computed' }],
    ...UNIT_ROW_GROUPS.map((g) => g.map((f) => ({
      key: f,
      label: fieldLabel(f),
      kind: fieldKind(f) === 'day_type' || fieldKind(f) === 'working_day' ? 'select' : fieldKind(f),
      bold: GRID_BOLD_FIELDS.includes(f),
      options: fieldKind(f) === 'day_type' ? dayTypeOptions : fieldKind(f) === 'working_day' ? workingDayOptions : undefined,
      // The unit's camera O/T and meal penalty are CALCULATED: never filled, a stored value is an override.
      noFill: UNIT_CALC_FIELDS.includes(f) || f === 'day_number', // Day # counts itself (2026-10-27)
      ...fieldEditorProps(f), // Travel (h): the 15-minute list, numbers only (2026-10-28)
    }))),
  ];

  /** The people on unit `uid` on `date`: `{ on, home, joined }` (a presentation count, no hours). */
  const crewOn = (uid, date) => {
    const on = (people || []).filter((p) => (p.days || []).some((d) => d.date === date && Number(d.unit_id) === uid));
    const home = on.filter((p) => Number(p.default_sub_department_id) === uid).length;
    return { on, home, joined: on.length - home };
  };

  /** The stored value the selected units AGREE on, `undefined` when they differ. */
  const storedFor = (field, date) => {
    if (selected.length === 0) return null;
    const vals = selected.map((u) => {
      const day = u.days && u.days[date];
      const v = day && day.values ? day.values[field] : null;
      return v === undefined ? null : v;
    });
    return vals.every((v) => String(v) === String(vals[0])) ? vals[0] : undefined;
  };

  // AS YOU TYPE (owner, 2026-10-26): a drafted call/wrap/meal re-asks the server's calculator.
  const previewRequests = selected.length !== 1 ? [] : dates
    .filter((date) => drafts[date] && UNIT_CALC_INPUTS.some((f) => Object.prototype.hasOwnProperty.call(drafts[date], f)))
    .map((date) => {
      const req = { key: date, sub_department_id: Number(selected[0].id) };
      for (const f of UNIT_CALC_INPUTS) {
        const drafted = Object.prototype.hasOwnProperty.call(drafts[date], f);
        req[f] = drafted ? payloadValue(fieldKind(f), drafts[date][f]) : storedFor(f, date);
      }
      return req;
    });
  const preview = useCalcPreview(previewRequests);

  /**
   * The auto wrap this column shows: the live preview's (a drafted call / working day — the server
   * works it out), else the selected units' own (`units[].days[d].auto`) when they all agree.
   */
  function autoWrapOn(date) {
    if (Object.prototype.hasOwnProperty.call(preview, date)) {
      const p = preview[date];
      return p && p.auto_wrap ? p.auto_wrap : null;
    }
    const autos = selected.map((u) => (u.days && u.days[date] && u.days[date].auto) || null);
    if (autos.length === 0 || autos.some((a) => !a) || autos.some((a) => a.unit_wrap !== autos[0].unit_wrap)) return null;
    return { wrap: autos[0].unit_wrap, explain: autos[0].explain };
  }

  function getCell(row, date) {
    const f = row.key;
    if (f === 'crew') {
      if (selected.length !== 1) return { text: null, tone: 'computed', editable: false, title: 'Pick one unit in Apply to to see its crew' };
      const { on, home, joined } = crewOn(Number(selected[0].id), date);
      return {
        text: joined ? `${home} + ${joined} joined` : String(on.length),
        tone: 'own', editable: true, opensPanel: true,
        title: `Who is on ${selected[0].name} this day — click to add or remove people`,
      };
    }
    const kind = fieldKind(f);
    const d = drafts[date] || {};
    if (Object.prototype.hasOwnProperty.call(d, f)) {
      const raw = d[f];
      const clearing = raw === null || (kind !== 'tick' && String(raw).trim() === '');
      // A pending CLEAR shows the cell EMPTY (owner 2026-10-26: "clear should
      // remove the values NOT just strike through them"); Cancel brings it back.
      return {
        text: clearing ? null : gridCellText(f, raw),
        tone: 'own',
        clearing,
        drafted: true,
        editable: true,
        raw,
        title: clearing
          ? (f === 'unit_wrap' ? 'Saving clears the typed wrap — it is then worked out from the call and working day' : 'Saving clears this value on the selected units')
          : 'Not saved yet',
        srNote: clearing ? 'will be cleared when saved' : 'not saved yet',
      };
    }
    const stored = storedFor(f, date);
    if (stored === undefined) {
      return { text: 'Mixed', tone: 'mixed', editable: true, raw: kind === 'tick' ? false : '', title: 'The selected units hold different values' };
    }
    if (f === 'unit_wrap' && stored === null) {
      // THE AUTO WRAP (owner 2026-10-28): no typed wrap → the SERVER's call + standard + lunch, shown
      // muted with an "auto" word; the editor opens on it, and typing a different time types it.
      const auto = autoWrapOn(date);
      if (auto) {
        return {
          text: auto.wrap, tone: 'computed', badge: 'auto', editable: true, raw: auto.wrap,
          // Nothing is stored: Monday's auto is nothing to copy (`inherited`), and Fill SKIPS a day
          // showing one (`fillSkip`, ruling 2A 2026-10-29) so it keeps following its call.
          fillSkip: true, inherited: true, clearable: false,
          title: auto.explain, srNote: 'worked out automatically',
        };
      }
    }
    if (UNIT_CALC_FIELDS.includes(f) && selected.length === 1) {
      const day = selected[0].days && selected[0].days[date];
      const live = Object.prototype.hasOwnProperty.call(preview, date);
      const dayCalc = live ? preview[date] : (day && day.calc);
      const calc = dayCalc ? dayCalc[f] : null;
      if (stored !== null) {
        return {
          text: kind === 'tick' ? (stored === true ? '✓' : 'No') : String(stored),
          tone: 'override', editable: true, raw: kind === 'tick' ? stored === true : String(stored),
          title: `Override for everyone on this unit — calculated ${calc ? (kind === 'tick' ? (calc.calculated ? 'a penalty' : 'no penalty') : calc.calculated) : 'nothing'}`,
          onReset: () => onCommit(row, date, null),
          srNote: 'override',
        };
      }
      // A calculated zero reads "–" (the mock), and is quiet in a folded summary.
      const text = !calc ? null : kind === 'tick' ? (calc.calculated ? '✓ auto' : '–') : (calc.calculated ? String(calc.calculated) : '–');
      return {
        text, tone: 'computed', editable: true, quiet: text === '–',
        raw: kind === 'tick' ? !!(calc && calc.calculated) : '',
        title: calc ? `${calc.explain}\nClick to override for everyone on this unit` : 'Calculated once the unit has a call and wrap',
        srNote: 'calculated',
      };
    }
    return {
      text: gridCellText(f, stored),
      tone: 'follow',
      editable: true,
      raw: kind === 'tick' ? stored === true : controlValue(kind, stored),
    };
  }

  /** A meal end before its start (the server's `meal_warning`) marks the Meal end cell amber ▲. */
  function cellWithMealCheck(row, date) {
    const cell = getCell(row, date);
    // FLAT (owner 2026-10-27): the times and O/T rows are not needed — greyed, not editable.
    if (FLAT_GREYED_FIELDS.includes(row.key) && workingDayNow(date) === 'flat') return { ...cell, text: OVERRIDE_LIKE.includes(row.key) ? '–' : null, tone: 'computed', editable: false, drafted: false, conflict: false, quiet: true, onReset: undefined, title: 'Flat day — not needed (a normal day’s pay, no hours measured)' };
    // Blank SWD/CWD = SWD: say so, faintly (the default, not a stored value).
    const restDay = dayTypeNow(date) === 'rest' || (dayTypeNow(date) == null && isWeekend(date) && !workedSignals(date));
    if (row.key === 'working_day' && restDay && !cell.drafted) return { ...cell, text: null, tone: 'follow', title: 'Rest day — no SWD/SCWD/CWD' };
    if (row.key === 'working_day' && selected.some((u) => workshopOnShootingUnit(workingDayNow(date), u))) return { ...cell, text: `▲ ${cell.text ?? 'Workshop'}`, tone: 'warn', title: WORKSHOP_ON_SET_WARNING, srNote: WORKSHOP_ON_SET_WARNING };
    if (row.key === 'working_day' && cell.text == null && !cell.drafted) return { ...cell, text: 'Flat', tone: 'computed', title: BLANK_FLAT_TITLE };
    if (row.key === 'day_type' && cell.text == null && !cell.drafted && isWeekend(date) && !workedSignals(date)) {
      // WEEKENDS DEFAULT TO REST (owner 2026-10-27) — shown, not stored; times or SWD/SCWD/CWD undo it.
      return { ...cell, text: 'Rest', tone: 'computed', title: 'Weekend — Rest by default. Pick a Day type, SWD/SCWD/CWD or enter times to work it.' };
    }
    if (row.key === 'day_number') {
      // THE DEPARTMENT'S DAY # (owner 2026-10-27): blank shows the department's number; a
      // timecard holding a different number is flagged.
      let dn = dayNumbers[date] ? dayNumbers[date].number : null;
      // Before saving, a changed Day # earlier in the week shifts the counted days after it.
      const earlier = dates.filter((d) => d < date && drafts[d] && 'day_number' in drafts[d] && String(drafts[d].day_number).trim() !== '');
      const d0 = earlier[earlier.length - 1];
      if (dn != null && d0 && dayNumbers[d0]) dn += Number(drafts[d0].day_number) - dayNumbers[d0].number;
      if (cell.text == null && !cell.drafted && dn != null) return { ...cell, text: String(dn), tone: 'computed', title: 'Department Day # (counted automatically)' };
      if (!cell.drafted && cell.text != null && dn != null && cell.text !== 'Mixed' && Number(cell.raw) !== dn) {
        return { ...cell, text: `▲ ${cell.text}`, tone: 'warn', title: `This timecard says ${cell.text}; the department's Day # is ${dn}` };
      }
      return cell;
    }
    if (row.key !== 'meal_end' || selected.length !== 1) return cell;
    const day = selected[0].days && selected[0].days[date];
    const dayCalc = Object.prototype.hasOwnProperty.call(preview, date) ? preview[date] : (day && day.calc);
    const warning = dayCalc && dayCalc.meal_warning;
    if (!warning) return cell;
    return { ...cell, text: `▲ ${cell.text ?? ''}`, tone: 'warn', drafted: false, title: warning, srNote: warning };
  }

  /** Owner 2026-10-26: "a rest day should clear the times" — picking Rest clears that column's times. */
  /** The Day # this column shows now: a draft, else the stored value, else the department's. */
  const shownDayNumber = (date) => {
    const d = drafts[date];
    if (d && Object.prototype.hasOwnProperty.call(d, 'day_number') && String(d.day_number).trim() !== '') return Number(d.day_number);
    const st = storedFor('day_number', date);
    if (st != null && st !== undefined) return Number(st);
    return dayNumbers[date] ? dayNumbers[date].number : null;
  };
  /** Owner 2026-10-27: a changed Day # is flagged and asked about (the rest of the week, every timecard). */
  function applyDayNumber(mode) {
    const ask = dayAsk;
    setDayAsk(null);
    if (!ask) return;
    const later = dates.filter((d) => d > ask.date);
    const pins = mode === 'only' ? later.map((d) => [d, shownDayNumber(d)]) : [];
    onCommit({ key: 'day_number' }, ask.date, ask.raw);
    if (mode === 'renumber') {
      // Later days go back to counting on from the new number (a stored one is cleared).
      for (const d of later) if (storedFor('day_number', d) != null || (drafts[d] && 'day_number' in drafts[d])) onCommit({ key: 'day_number' }, d, '');
    } else {
      // Only this day: later days keep the numbers they show now.
      for (const [d, n] of pins) if (n != null) onCommit({ key: 'day_number' }, d, String(n));
    }
  }

  // SWD/SCWD/CWD and Rest exclude each other (owner 2026-10-27): picking one on a Rest day
  // sets the Day type back to Not set.
  const workingDayNow = (date) => {
    const d = drafts[date];
    if (d && Object.prototype.hasOwnProperty.call(d, 'working_day')) return d.working_day;
    const st = storedFor('working_day', date);
    return st === undefined ? null : st;
  };
  const dayTypeNow = (date) => {
    const d = drafts[date];
    if (d && Object.prototype.hasOwnProperty.call(d, 'day_type')) return d.day_type;
    const st = storedFor('day_type', date);
    return st === undefined ? null : st;
  };
  function workedSignals(date) {
    const d = drafts[date] || {};
    return ['unit_call', 'unit_wrap', 'working_day'].some((f) => {
      if (Object.prototype.hasOwnProperty.call(d, f)) return d[f] != null && String(d[f]).trim() !== '';
      const st = storedFor(f, date);
      return st != null;
    });
  }

  function commitWithRest(row, date, raw) {
    if (row.key === 'working_day' && raw != null && String(raw).trim() !== '' && dayTypeNow(date) === 'rest') {
      onCommit({ key: 'day_type' }, date, '');
    }
    if (row.key === 'day_number' && raw != null && String(raw).trim() !== '') {
      const from = shownDayNumber(date);
      if (from == null || Number(raw) !== from) { setDayAsk({ date, raw: String(raw).trim(), from }); return; }
    }
    onCommit(row, date, raw);
    if (row.key !== 'day_type') return;
    if (raw === 'rest') {
      for (const f of REST_CLEARS_FIELDS) if (UNIT_ROW_GROUPS.some((g) => g.includes(f))) onCommit({ key: f }, date, '');
      return;
    }
    // PREP AUTO-FILL (owner 2026-10-26, "when Prep is picked"): the unit's usual Prep day
    // (the server's `prep_usual` — the most common whole day over the last 4 weeks) fills
    // the BLANK times only, as unsaved values. Several units: only when they agree.
    if (raw !== 'prep' || selected.length === 0) return;
    const usual = selected[0].prep_usual;
    if (!usual || !selected.every((u) => JSON.stringify(u.prep_usual) === JSON.stringify(usual))) return;
    const drafted = drafts[date] || {};
    for (const f of PREP_FILL_FIELDS) {
      if (usual[f] == null || Object.prototype.hasOwnProperty.call(drafted, f) || storedFor(f, date) !== null) continue;
      onCommit({ key: f }, date, usual[f]);
    }
  }

  function onCommit(row, date, raw) {
    setSavedNote(null);
    setDrafts((prev) => {
      const day = { ...(prev[date] || {}) };
      const kind = fieldKind(row.key);
      const stored = storedFor(row.key, date);
      const storedRaw = stored === undefined ? undefined : (kind === 'tick' ? stored === true : controlValue(kind, stored));
      if (UNIT_CALC_FIELDS.includes(row.key) && stored !== undefined) {
        // A calculated row: blank (stored null) is "calculate", so an explicit false/0 is a real override.
        const next = raw === null || (kind !== 'tick' && String(raw).trim() === '') ? null : raw;
        const same = next === null ? stored === null : stored !== null && String(next) === String(stored);
        if (same) delete day[row.key]; else day[row.key] = next;
      } else if (raw === storedRaw) delete day[row.key]; else day[row.key] = raw;
      const next = { ...prev };
      if (Object.keys(day).length === 0) delete next[date]; else next[date] = day;
      return next;
    });
  }


  async function save() {
    if (unitIds.length === 0) { setError('Pick at least one unit in Apply to, then save.'); return false; }
    if (!touched) return true;
    setBusy(true);
    setError(null);
    setSavedNote(null);
    const days = dates.filter((d) => drafts[d]).map((work_date) => {
      const values = {};
      for (const [f, raw] of Object.entries(drafts[work_date])) values[f] = payloadValue(fieldKind(f), raw);
      return { work_date, values };
    });
    try {
      const ids = unitIds.map(Number);
      const r = await runEditGate(
        { kind: 'unit', sub_department_ids: ids, days },
        (editReason) => saveUnitDays({ unitIds: ids, days, editReason }),
        { describe: `${selected.map((u) => u.name).join(', ')} · ${days.length === 1 ? dayLabel(days[0].work_date) : `${days.length} days`}` },
      );
      // "Keep editing": nothing sent, the typed values stay.
      if (r && r.cancelled) return false;
      if (!r.ok) { setError(saveErrorText(r.body, 'Could not save the unit timecard.')); return false; }
      setHistoryTick((t) => t + 1);
      const conflicts = (r.body && r.body.conflicts) || [];
      setDrafts({});
      setSavedNote(conflicts.length
        ? `Saved. ${conflicts.length} ${conflicts.length === 1 ? 'value' : 'values'} now differ from the unit — they are marked ▲ under All users.`
        : 'Saved. Nobody’s own values disagree.');
      onSaved?.(conflicts);
      return true;
    } catch (e) {
      setError('Could not reach the server. Your entries are still here — try again.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  const discard = useCallback(() => { setDrafts({}); setError(null); }, []);
  useEffect(() => {
    if (saveRef) saveRef.current = save;
    if (discardRef) discardRef.current = discard;
  });

  const n = headCount(unitIds.map(Number));
  return (
    <section data-testid="unit-timecard-card" aria-label="Unit timecard"
      className={`rounded-lg border border-gray-200 bg-white p-2 md:p-3 dark:border-gray-700 dark:bg-gray-800 ${controlled ? 'max-md:flex max-md:flex-col' : ''}`}>
      {/* Apply to · reach · Cancel / Save — the buttons sit UP here, top-right
          (owner 2026-10-25: the timecard and its buttons on one laptop screen).
          📱 Controlled, below `md`: this row is only the pinned bar now, so it goes LAST
          and its spacer pads the card's end instead of opening a gap at the top. */}
      <div className={`mb-2 flex flex-wrap items-center gap-x-3 gap-y-2 ${controlled ? 'max-md:order-last max-md:mb-0' : ''}`}>
        <span className={`text-sm font-medium text-gray-700 dark:text-gray-300 ${controlled ? 'max-md:hidden' : ''}`}>Apply to</span>
        <div className={controlled ? 'max-md:hidden' : 'contents'}>
        <ChecklistDropdown
          testId="unit-timecard-apply-to"
          ariaLabel="Apply to"
          placeholder="Choose units…"
          sections={[{ key: 'units', items: ordered }]}
          selected={unitIds}
          onChange={(ids) => { setPicked(ids.map(Number)); setSavedNote(null); }}
          countNoun={(c) => `${c} ${c === 1 ? 'person' : 'people'}`}
        />
        </div>
        <span className={`text-sm text-gray-600 dark:text-gray-400 ${controlled ? 'max-md:hidden' : ''}`} data-testid="unit-timecard-reach">
          {n} {n === 1 ? 'person' : 'people'}<span className="max-md:hidden"> · anyone with their own values keeps them · blank = leave as it is</span>
        </span>
        <button type="button" data-testid="unit-timecard-history" className={`${BTN_XS.secondary} max-md:min-h-[44px]`}
          aria-expanded={historyOpen} disabled={unitIds.length === 0} onClick={() => setHistoryOpen((o) => !o)}>
          History
        </button>
        {/* 📱 Below `md` the whole action row is the pinned bottom bar (owner 2026-10-27, mock B:
            Submit week in the thumb zone); Cancel / Save join it only while changed. */}
        <PhoneActionBar pinned={touched || !!onSubmitWeek} fill className="ml-auto flex items-center gap-2" testId="unit-timecard-bar">
          <div className={`flex items-center gap-2 ${touched ? 'max-md:contents' : 'max-md:hidden'}`} data-testid="unit-timecard-actions">
            <button type="button" className={BTN.secondary} disabled={busy || !touched} onClick={discard} data-testid="unit-timecard-cancel">Cancel</button>
            <button type="button" data-testid="unit-timecard-save" className={BTN.confirm} disabled={busy || !touched || unitIds.length === 0} onClick={save}>
              {busy ? 'Saving…' : unitIds.length === 0 ? 'Save timecard' : `Save to ${unitIds.length} ${unitIds.length === 1 ? 'unit' : 'units'}`}
            </button>
          </div>
          {onSubmitWeek && (
            // SUBMIT WEEK (owner 2026-10-26): saves first ("Submit should also save"), then the
            // page checks for missing details and submits the people whose HOME unit is in Apply to.
            <button type="button" data-testid="unit-timecard-submit" className={BTN.confirm}
              disabled={busy || submitBusy || unitIds.length === 0}
              title="Save, then submit this week for everyone in these units — crew can no longer change it"
              onClick={async () => { if (touched && !(await save())) return; onSubmitWeek(unitIds.map(Number)); }}>
              {unitIds.length > 1 ? `Submit week (${unitIds.length} units)` : 'Submit week'}
            </button>
          )}
        </PhoneActionBar>
      </div>

      <WeekTimecardGrid
        dates={dates}
        today={today}
        // THE * (owner 2026-10-30): a unit's Day type and Call flow to everyone following it.
        groups={markRequiredRows(groups, dates.some((d) => PICKED_WORKING_DAYS.includes(workingDayNow(d))))}
        getCell={cellWithMealCheck}
        onCommit={commitWithRest}
        fill
        onConflict={() => true}
        renderConflict={({ row, date, close }) => (row.key === 'crew' && selected.length === 1 ? (
          <CrewPicker unit={selected[0]} date={date} people={people || []} units={units} runEditGate={runEditGate}
            onApplied={() => { close(); onSaved?.([]); }} onCancel={close} />
        ) : null)}
        idPrefix="unit-timecard"
        onStepWeek={onStepWeek}
        requestedWeek={requestedWeek}
        ariaLabel="Unit timecard"
        // FLAT WEEK (owner 2026-10-27): the times fold away until a day picks SWD/SCWD/CWD or has a time.
        timesFold={{
          label: 'Times',
          auto: dates.some((d) => ['swd', 'scwd', 'cwd', 'workshop'].includes(workingDayNow(d)))
            || groups.some((g) => g.length && g.every((r) => TIMES_FOLD_FIELDS.includes(r.key))
              && g.some((r) => dates.some((d) => { const c = cellWithMealCheck(r, d); return c.drafted || c.conflict || c.text != null; }))),
        }}
      />

      {historyOpen && dates.length > 0 && (
        <div className="mt-3 space-y-3" data-testid="unit-history">
          {selected.map((u) => (
            <TimesheetHistoryPanel key={u.id} kind="unit" unitId={Number(u.id)} from={dates[0]} to={dates[dates.length - 1]}
              units={units} title={`History · ${u.name} · w/c ${formatDateDayMonth(dates[0])}`} refreshSignal={historyTick}
              onClose={() => setHistoryOpen(false)} testId={`unit-history-panel-${u.id}`} />
          ))}
        </div>
      )}
      {editReasonDialog}

      <ConfirmDialog
        open={!!dayAsk}
        title={dayAsk ? `Change Day # on ${dayLabel(dayAsk.date)}${dayAsk.from != null ? ` from ${dayAsk.from}` : ''} to ${dayAsk.raw}?` : ''}
        confirmLabel="Renumber the rest of the week"
        altLabel="Only this day"
        altVariant="secondary"
        onAlt={() => applyDayNumber('only')}
        onConfirm={() => applyDayNumber('renumber')}
        onCancel={() => setDayAsk(null)}
      >
        <p className="text-left text-sm text-gray-700 dark:text-gray-300" data-testid="day-number-ask">
          Day # is shared by every timecard in the department, so this changes it for every unit when you save.
          Renumber carries on counting from the new number for the rest of the week; Only this day leaves the later days as they are.
        </p>
      </ConfirmDialog>

      {error && <p role="alert" className="mt-2 text-sm text-red-700 dark:text-red-300">{error}</p>}
      {savedNote && <p className="mt-2 text-sm text-gray-700 dark:text-gray-300" data-testid="unit-timecard-saved">{savedNote}</p>}
    </section>
  );
}

/**
 * THE CREW PICKER (calculated overtime, owner-approved mock 2026-10-26): who
 * is on this unit THIS day. The unit's own people are listed ticked; "Join
 * from other sub-departments" lists everyone else with a search. Ticking
 * someone sets THEIR Unit for this day only, through the EXISTING manager
 * person-day write (`PUT /api/timesheets/people/:lineage/days/:date`
 * `{ sub_department_id }` — the same as editing their Unit cell, same gate,
 * no new route); unticking a joiner puts them back on their own unit. A
 * home member cannot be unticked here (which unit would they go to?) — move
 * them with their Unit cell. A submitted week is read-only.
 */
function CrewPicker({ unit, date, people, units, runEditGate, onApplied, onCancel }) {
  const uid = Number(unit.id);
  const dayOf = (p) => (p.days || []).find((d) => d.date === date) || null;
  const isOn = (p) => { const d = dayOf(p); return !!d && Number(d.unit_id) === uid; };
  const [ticks, setTicks] = useState(() => Object.fromEntries(people.map((p) => [p.lineage_id, isOn(p)])));
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const home = people.filter((p) => Number(p.default_sub_department_id) === uid);
  const others = people.filter((p) => Number(p.default_sub_department_id) !== uid);
  const q = query.trim().toLowerCase();
  const shownOthers = others.filter((p) => isOn(p) || ticks[p.lineage_id] || (q !== '' && String(p.name).toLowerCase().includes(q)));
  const changed = people.filter((p) => ticks[p.lineage_id] !== isOn(p));

  const apply = async () => {
    setBusy(true);
    setError(null);
    try {
      for (const p of changed) {
        // eslint-disable-next-line no-await-in-loop
        const body = { sub_department_id: ticks[p.lineage_id] ? uid : null };
        // A manager person-unit write like any other: the reason prompt when it reaches a SUBMITTED
        // week (a 6th/7th run can carry it there) — review round 2026-10-30.
        const r = await runEditGate(
          { kind: 'person', lineage_id: p.lineage_id, date, ...body },
          (editReason) => saveManagerPersonDay(p.lineage_id, date, editReason ? { ...body, edit_reason: editReason } : body),
          { describe: `${p.name} · ${dayLabel(date)} · Unit ${unit.name}` },
        );
        // "Keep editing": this person and everyone after them are left as ticked, nothing more sent.
        if (r && r.cancelled) return;
        if (!r.ok) { setError(`${p.name}: ${(r.body && r.body.error) || 'could not be moved'}`); return; }
      }
      onApplied();
    } catch (e) {
      setError('Could not reach the server. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const row = (p, lockedOn) => {
    const submitted = p.status === 'submitted';
    const disabled = busy || submitted || (lockedOn && isOn(p));
    const other = Number(p.default_sub_department_id) !== uid ? unitNameIn(units, p.default_sub_department_id) : null;
    return (
      <label key={p.lineage_id} className={`flex min-h-[44px] md:min-h-0 items-center gap-2 py-0.5 text-sm text-gray-800 dark:text-gray-200 ${disabled ? 'opacity-60' : 'cursor-pointer'}`}
        title={submitted ? 'Their week is approved — reopen it first' : lockedOn && isOn(p) ? 'Their own unit — move them with their Unit cell' : undefined}>
        <input type="checkbox" checked={!!ticks[p.lineage_id]} disabled={disabled}
          onChange={(e) => setTicks((t) => ({ ...t, [p.lineage_id]: e.target.checked }))}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
          data-testid={`crew-picker-${p.lineage_id}`} />
        <span className="truncate">{p.name}</span>
        {other && <span className="truncate text-xs text-gray-500 dark:text-gray-400">· {other}</span>}
      </label>
    );
  };

  return (
    <div role="dialog" aria-label={`${unit.name} crew, ${dayLabel(date)}`} data-testid="crew-picker"
      className="w-72 max-w-[calc(100vw-1rem)] rounded-lg border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-gray-800">
      <div className="mb-2 text-sm font-medium text-gray-900 dark:text-gray-100">{unit.name} · {dayLabel(date)}</div>
      <div className="text-xs text-gray-500 dark:text-gray-400">{unit.name}</div>
      <div className="max-h-48 overflow-y-auto">{home.length ? home.map((p) => row(p, true)) : <p className="py-1 text-xs text-gray-500 dark:text-gray-400">Nobody has this as their unit.</p>}</div>
      <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">Join from other sub-departments</div>
      <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search crew…" aria-label="Search crew to join"
        className={`${FORM_INPUT_FIELD} my-1`} data-testid="crew-picker-search" />
      <div className="max-h-48 overflow-y-auto">{shownOthers.map((p) => row(p, false))}</div>
      <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">Ticking someone sets their Unit to {unit.name} for this day only — same as editing their Unit cell.</p>
      {error && <p role="alert" className="mt-1 text-xs text-red-700 dark:text-red-300">{error}</p>}
      <div className="mt-2 flex justify-end gap-2">
        <button type="button" className={BTN_XS.secondary} onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="button" className={BTN_XS.primary} onClick={apply} disabled={busy || changed.length === 0} data-testid="crew-picker-apply">
          {busy ? 'Saving…' : changed.length ? `Apply (${changed.length})` : 'Apply'}
        </button>
      </div>
    </div>
  );
}
